import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, Settings, LayoutGrid, Users, FileText, Trash2, AlertCircle, Loader2, AlertTriangle } from "lucide-react";
import Approvals from "./Approvals";
import Orphans from "./Orphans";
import Customers from "./Customers";
import Contracts from "./Contracts";
import ReconciliationModal from "../agents/ReconciliationModal";
import RevSyncAgentSettings from "../agents/RevSyncAgentSettings";
import { revSyncService } from "../../services/RevSyncService";
import { useToastContext } from "../../context/ToastContext";
import { isStaging, isDevelopment } from "../../utils/Environment";

export type RevSyncHubTab =
  | "contract-management"
  | "orphans"
  | "customers"
  | "contracts";

const TABS: { id: RevSyncHubTab; label: string; icon: React.ReactNode }[] = [
  {
    id: "contract-management",
    label: "Contract Management",
    icon: <LayoutGrid className="w-4 h-4" />,
  },
  { id: "orphans", label: "Orphans", icon: <AlertTriangle className="w-4 h-4" /> },
  { id: "customers", label: "Customers", icon: <Users className="w-4 h-4" /> },
  { id: "contracts", label: "Contracts", icon: <FileText className="w-4 h-4" /> },
];

const formatStatsCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const RevSyncHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToastContext();
  const tabParam = searchParams.get("tab") as RevSyncHubTab | null;
  const activeTab: RevSyncHubTab =
    tabParam && TABS.some((t) => t.id === tabParam)
      ? tabParam
      : "contract-management";

  const [hubStats, setHubStats] = useState<{
    totalMRR: number;
    totalARR: number;
    loading: boolean;
  }>({ totalMRR: 0, totalARR: 0, loading: true });
  const [isReconcileOpen, setIsReconcileOpen] = useState(false);
  const [isReconcileClosing, setIsReconcileClosing] = useState(false);
  const [isReconcileOpening, setIsReconcileOpening] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isConfigClosing, setIsConfigClosing] = useState(false);
  const [isConfigOpening, setIsConfigOpening] = useState(false);
  const [isClearDataModalOpen, setIsClearDataModalOpen] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);

  const setTab = (tab: RevSyncHubTab) => {
    setSearchParams({ tab });
  };

  const openReconcile = () => {
    setIsReconcileClosing(false);
    setIsReconcileOpening(true);
    setIsReconcileOpen(true);
    setTimeout(() => setIsReconcileOpening(false), 10);
  };

  const closeReconcile = () => {
    if (isReconcileClosing) return;
    setIsReconcileClosing(true);
    setTimeout(() => {
      setIsReconcileOpen(false);
      setIsReconcileClosing(false);
    }, 150);
  };

  const openConfig = () => {
    setIsConfigClosing(false);
    setIsConfigOpening(true);
    setIsConfigOpen(true);
    setTimeout(() => setIsConfigOpening(false), 10);
  };

  const closeConfig = () => {
    if (isConfigClosing) return;
    setIsConfigClosing(true);
    setTimeout(() => {
      setIsConfigOpen(false);
      setIsConfigClosing(false);
    }, 150);
  };

  const handleClearData = async () => {
    setIsClearingData(true);
    try {
      const { data, message } = await revSyncService.clearData();
      const counts = data.deleted;
      const countMessages = [
        `${counts.contracts} contract${counts.contracts !== 1 ? "s" : ""}`,
        `${counts.globalCustomers} customer${counts.globalCustomers !== 1 ? "s" : ""}`,
        `${counts.reconciliationRuns} reconciliation run${counts.reconciliationRuns !== 1 ? "s" : ""}`,
        `${counts.fieldMappings} field mapping${counts.fieldMappings !== 1 ? "s" : ""}`,
        `${counts.hubSpotObjectMappings} object mapping${counts.hubSpotObjectMappings !== 1 ? "s" : ""}`,
        `${counts.renewalReminders} renewal reminder${counts.renewalReminders !== 1 ? "s" : ""}`,
        `${counts.hubSpotSyncLogs} sync log${counts.hubSpotSyncLogs !== 1 ? "s" : ""}`,
      ].filter((msg) => !msg.startsWith("0"));
      const successMessage = `Data cleared successfully. Deleted: ${countMessages.join(", ")}.`;
      showToast("success", successMessage);
      setIsClearDataModalOpen(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to clear data";
      showToast("error", errorMessage);
    } finally {
      setIsClearingData(false);
    }
  };

  // Sync URL to tab on mount if missing
  useEffect(() => {
    if (!searchParams.has("tab")) {
      setSearchParams({ tab: "contract-management" }, { replace: true });
    }
  }, []);

  // Load hub stats (MRR/ARR) for header
  useEffect(() => {
    let cancelled = false;
    setHubStats((prev) => ({ ...prev, loading: true }));
    revSyncService
      .listContracts({ page: 1, limit: 5000, status: "active" })
      .then(({ data }) => {
        if (cancelled) return;
        const totalMRR = data.contracts.reduce((sum, c) => sum + c.mrr, 0);
        const totalARR = data.contracts.reduce((sum, c) => sum + c.arr, 0);
        setHubStats({ totalMRR, totalARR, loading: false });
      })
      .catch(() => {
        if (!cancelled) setHubStats((prev) => ({ ...prev, loading: false }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="w-full min-h-screen bg-background-default dark:bg-background-dark flex flex-col">
      {/* Header: title + stats + top-right icons */}
      <div className="flex-shrink-0 border-b border-divider dark:border-divider-dark bg-background-paper dark:bg-background-paperDark">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <h1 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                RevSync Hub
              </h1>
              <div className="flex items-center gap-4 border-l border-divider dark:border-divider-dark pl-6">
                {hubStats.loading ? (
                  <span className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Stats…
                  </span>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-text-secondary dark:text-text-secondaryDark">
                        MRR
                      </span>
                      <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark tabular-nums">
                        {formatStatsCurrency(hubStats.totalMRR)}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-text-secondary dark:text-text-secondaryDark">
                        ARR
                      </span>
                      <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark tabular-nums">
                        {formatStatsCurrency(hubStats.totalARR)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={openReconcile}
                className="p-2 rounded-lg text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
                aria-label="Reconcile"
                title="Reconcile"
              >
                <BarChart3 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={openConfig}
                className="p-2 rounded-lg text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
                aria-label="Config"
                title="Config"
              >
                <Settings className="w-4 h-4" />
              </button>
              {(isStaging || isDevelopment) && (
                <button
                  type="button"
                  onClick={() => setIsClearDataModalOpen(true)}
                  className="p-2 rounded-lg text-text-secondary dark:text-text-secondaryDark hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  aria-label="Clear data"
                  title="Clear data (local/staging only)"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Tabs — Discord-inspired: pill + left accent for selected */}
          <nav
            className="flex items-center gap-0.5 mt-4"
            aria-label="RevSync sections"
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`relative inline-flex items-center gap-2 pl-4 pr-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-white/10 dark:bg-white/10 text-text-primary dark:text-text-primaryDark"
                    : "text-text-secondary dark:text-text-secondaryDark hover:bg-white/5 dark:hover:bg-white/5 hover:text-text-primary dark:hover:text-text-primaryDark"
                }`}
              >
                {activeTab === tab.id && (
                  <span
                    className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-md bg-text-primary dark:bg-text-primaryDark"
                    aria-hidden
                  />
                )}
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto min-h-0">
        {activeTab === "contract-management" && (
          <div className="w-full max-w-[1400px] mx-auto px-6 py-6">
            <div className="bg-background-paper dark:bg-background-paperDark rounded-2xl border border-divider/60 dark:border-divider-dark/60 p-4">
              <Approvals embedded />
            </div>
          </div>
        )}
        {activeTab === "orphans" && (
          <div className="w-full max-w-[1400px] mx-auto px-6 py-6">
            <div className="bg-background-paper dark:bg-background-paperDark rounded-2xl border border-divider/60 dark:border-divider-dark/60 p-4">
              <Orphans embedded />
            </div>
          </div>
        )}
        {activeTab === "customers" && <Customers />}
        {activeTab === "contracts" && <Contracts />}
      </div>

      <ReconciliationModal
        isOpen={isReconcileOpen}
        onClose={closeReconcile}
        isClosing={isReconcileClosing}
        isOpening={isReconcileOpening}
      />

      <RevSyncAgentSettings
        isOpen={isConfigOpen}
        onClose={closeConfig}
        isClosing={isConfigClosing}
        isOpening={isConfigOpening}
      />

      {/* Clear Data Confirmation Modal — local/staging only */}
      {isClearDataModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
            onClick={() => !isClearingData && setIsClearDataModalOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-background-paper dark:bg-background-paperDark rounded-lg shadow-2xl max-w-md w-full border border-divider dark:border-divider-dark"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                      Clear All RevSync Data
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                      This will permanently delete all RevSync data including
                      contracts, customers, reconciliation runs, field mappings,
                      and sync logs. The agent will be uninstalled and settings
                      will be reset. This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsClearDataModalOpen(false)}
                    disabled={isClearingData}
                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleClearData}
                    disabled={isClearingData}
                    className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
                  >
                    {isClearingData ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Clearing...
                      </>
                    ) : (
                      "Clear Data"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RevSyncHub;
